import { NgClass } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import { toAssistantForAnalytics } from '@shared';
// Aliased to match the builder surface and the barrel's documented reason for
// withholding the bare name (it collides with `@angular/forms`' `Validators`).
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import type { FileUploadedEvent } from '../../../analytics/analytics.events';
import * as fromAnalyticsHelper from '../../../analytics/analytics.helper';
import { AnalyticsService } from '../../../analytics/analytics.service';
import * as fromFeatureFlags from '../../../feature-flags';
import * as fromLayout from '../../../layout';
import { ViewTransitionTracker } from '../../../view-transitions';
import { MAX_USER_INPUT_LENGTH } from '../../chats.constants';
import { ChatsHelper } from '../../chats.helper';
import * as fromShared from '../../shared';
import { Validators as UploadValidators } from '../../shared/directives/file-upload-v2/file-upload-v2.helpers';
import type { SubmitDisabledResult } from '../../shared/selectors/conversation-prompt.selectors';
import {
  ChatActions,
  ChatsActions,
  chatsFeature,
  ChatsState,
  ChatWithMessagesActions,
} from '../../store';
// Concrete file, not the store barrel: the service is not exported there.
import { ChatCreationService } from '../../store/chat-creation.service';
import * as fromMessagesListing from '../messages-listing';
import { PromptInputPlusMenuComponent } from './prompt-input-plus-menu.component';
import {
  isPromptTextMissing,
  toSendableMessageContent,
} from './prompt-input-send.helpers';
import {
  autoSubmitBatchKey,
  deriveRemovedPromptInputFlags,
  isSendAgainShown,
} from './removed-prompt-input.helpers';
import { SpeechToTextListeningComponent } from './speech-to-text';
import { SpeechToTextAzureService } from './speech-to-text/azure/speech-to-text-azure.service';
import { SpeechToTextDeepgramService } from './speech-to-text/deepgram';
import { SpeechToTextServiceInterface } from './speech-to-text/prompt-input-speech-to-text.interfaces';
import { appendDictationToInput } from './speech-to-text/speech-to-text.helper';

const TEXTAREA_MIN_HEIGHT = 24;
const TEXTAREA_MAX_HEIGHT = 240;

@Component({
  selector: 'squadbox-conversation-prompt-input-smart',
  imports: [
    FormsModule,
    TranslateModule,
    MatTooltipModule,
    fromSeriousSystem.IconButtonDirective,
    fromSeriousSystem.UseUIBasicIconDirective,
    fromSeriousSystem.UseArrowIconDirective,
    fromSeriousSystem.FileChipComponent,
    SpeechToTextListeningComponent,
    NgClass,
    fromShared.FileUploadTriggerDirective,
    PromptInputPlusMenuComponent,
  ],
  template: `
    <div class="flex flex-col gap-4 w-full relative">
      <!-- CUSTOM TOOLTIP -->
      @let tooltipOverPrompt = tooltipOverPromptText();
      @if (tooltipOverPrompt) {
        <div
          class="
            place-items-center
            absolute -top-2.5 left-1/2 transform -translate-x-1/2 -translate-y-full
            mb-2 z-20
            max-w-full
          "
        >
          @if (tooltipOverPrompt) {
            <!-- Tooltip content -->
            <div
              class="
                px-2 py-1 bg-neutral-700 rounded tablet-portrait:max-w-70
                text-sm leading-5 font-semibold text-white text-center
              "
            >
              {{ tooltipOverPrompt | translate }}
            </div>
            <!-- Tooltip pointer -->
            <div
              class="
              w-2 h-2 bg-neutral-700 rotate-45 -z-1
              absolute -bottom-2.5 left-1/2 transform -translate-x-1/2 -translate-y-full
            "
            ></div>
          }
        </div>
      }

      <!-- INPUT -->
      <div
        class="
          flex flex-col bg-shades-white tablet-landscape:shadow
          border border-neutral-200 rounded-3xl overflow-hidden
          transition-colors duration-200 ease-in-out
        "
        [ngClass]="{
          'hover:border-primary-300 focus-within:!border-primary-500':
            !isChatUnavailable(),
          '!bg-slate-50 !border-primary-500': sttState() !== 'inactive',
          'opacity-60': isChatUnavailable(),
        }"
      >
        <!-- SOURCES - FILE CHIPS -->
        @if (documentFileChips().length) {
          <div class="flex items-end justify-start pr-1 py-0">
            <div
              class="
                flex flex-wrap gap-2 grow basis-0 pl-2 pt-2
                max-h-24 overflow-x-clip overflow-y-auto
              "
            >
              @for (
                documentFileChip of documentFileChips();
                track documentFileChip.uuid
              ) {
                <sd-file-chip
                  [filename]="documentFileChip.filename"
                  [fileType]="documentFileChip.fileType"
                  [isLoading]="documentFileChip.isLoading"
                  [isError]="documentFileChip.error"
                  rightIcon="close"
                  (rightIconClicked)="removeFile(documentFileChip.uuid)"
                  (chipClicked)="openImageOverlayFromComposer(documentFileChip)"
                ></sd-file-chip>
              }
            </div>
          </div>
        }

        <!-- TEXTAREA -->
        <label class="block py-4 cursor-text">
          <textarea
            #textarea
            [value]="userInput()"
            (input)="handleInput($event)"
            (keydown)="handleKeydown($event)"
            (paste)="onPaste()"
            [disabled]="sttState() !== 'inactive' || isChatUnavailable()"
            placeholder="{{ inputPlaceholder() | translate }}"
            class="
              block min-h-6 max-h-80 w-full px-4 bg-transparent
              resize-none overflow-y-auto
              transition-[height] duration-200
              typo-p2 text-neutral-700 placeholder:text-neutral-500 leading-6
              focus:outline-hidden focus:caret-primary-500 disabled:cursor-not-allowed
            "
          ></textarea>
        </label>

        <div class="flex p-2 justify-between items-stretch">
          <!-- LEFT -->
          @if (sttState() !== 'inactive') {
            <button
              sdIconButton
              variant="tonal"
              color="neutral"
              class="fade-in bg-neutral-200 hover:bg-neutral-300 focus:bg-neutral-300 active:bg-neutral-400"
              [size]="isMobile() ? 'sm' : 'xs'"
              (click)="handleClickAbortSttCTA()"
              [disabled]="isAbortSttDisabled()"
              [matTooltip]="'PROMPT_INPUT.TOOLTIP.STOP' | translate"
              [matTooltipDisabled]="isAbortSttDisabled()"
              matTooltipPosition="above"
            >
              <svg sdUseUIBasicIcon="close"></svg>
            </button>
          } @else if (ffMcpConnectors()) {
            <!-- Reuse the upload directive inside the composer menu. -->
            <squadbox-prompt-input-plus-menu
              [fileUploadStore]="fileUploadStore"
              [allowMultipleFiles]="remainingUploadSlots() > 1"
              [fileUploadDisabled]="fileUploadDisabled().disabled"
              [isMobile]="isMobile()"
              [surface]="connectorSurface()"
              [saving]="connectorsSaving()"
              [chatUuid]="selectedChatUuid()"
              [disabledConnectorIds]="chatDisabledConnectorIds()"
              (disabledConnectorIdsChange)="
                handleChangeDisabledConnectors($event)
              "
            ></squadbox-prompt-input-plus-menu>

            <!-- Keep a mounted upload trigger for empty-state and floating-button actions. -->
            <!--
              SB-3537 #1442: [accept] is bound from the store's own computed,
              so the picker offers exactly what the validator accepts and the
              two cannot disagree about .xlsx on a toggle-off skill. Courtesy
              only — drag-and-drop bypasses accept entirely (see the window
              drop handler on conversation.page.ts) and is refused by the
              store's validator instead.
            -->
            <button
              #fileUploadButton
              type="button"
              squadboxFileUploadTrigger
              class="hidden"
              tabindex="-1"
              aria-hidden="true"
              [store]="fileUploadStore"
              [accept]="fileUploadStore.acceptAttribute()"
              [multiple]="remainingUploadSlots() > 1"
            ></button>
          } @else {
            <!--
              Connectors off (ff-mcp-connectors): the composer keeps its direct attach button.
              [accept] follows the store for the same reason as the hidden trigger above
              (SB-3537 #1442).
            -->
            <button
              #fileUploadButton
              sdIconButton
              squadboxFileUploadTrigger
              variant="icon"
              color="neutral"
              class="fade-in"
              [store]="fileUploadStore"
              [accept]="fileUploadStore.acceptAttribute()"
              [multiple]="remainingUploadSlots() > 1"
              [size]="isMobile() ? 'sm' : 'xs'"
              [matTooltip]="fileUploadTooltipText()"
              matTooltipPosition="above"
              [disabled]="fileUploadDisabled().disabled"
            >
              <svg sdUseUIBasicIcon="attach"></svg>
            </button>
          }

          <!-- MIDDLE -->
          <div
            class="flex-1 ml-2 mr-1 overflow-x-hidden flex items-center justify-end"
          >
            @if (
              sttState() === 'starting' ||
              sttState() === 'transcripting' ||
              sttState() === 'finalizing'
            ) {
              <squadbox-speech-to-text-listening
                class="fade-in"
                [isLoading]="sttState() !== 'transcripting'"
              ></squadbox-speech-to-text-listening>
            }
          </div>

          <!-- RIGHT -->
          <div class="flex justify-end items-center gap-3">
            @if (sttState() !== 'inactive') {
              <div
                class="fade-in min-w-10 font-small-text font-semibold text-caption text-right text-neutral-700 tabular-nums"
              >
                {{ recordingTimeLabel() }}
              </div>

              <button
                sdIconButton
                variant="filled"
                color="primary"
                [size]="isMobile() ? 'sm' : 'xs'"
                (click)="handleClickFinalizeSttCTA()"
                [disabled]="isFinalizeSttDisabled()"
                class="fade-in transition-all duration-300 ease-in-out"
              >
                <svg class="fade-in" sdUseUIBasicIcon="check"></svg>
              </button>
            } @else {
              <button
                sdIconButton
                class="fade-in"
                variant="icon"
                color="neutral"
                [size]="isMobile() ? 'sm' : 'xs'"
                [disabled]="isSpeechButtonDisabled()"
                (click)="handleClickStartSttCTA()"
                [matTooltip]="speechButtonTooltip() | translate"
                matTooltipPosition="above"
              >
                <svg sdUseUIBasicIcon="mic"></svg>
              </button>

              @let _submitTooltip = submitButtonTooltip();
              @let _submitDisabled = submitDisabledResult();
              <button
                sdIconButton
                variant="filled"
                color="primary"
                [size]="isMobile() ? 'sm' : 'xs'"
                [disabled]="_submitDisabled.disabled"
                (click)="handleClickSubmitPromptCTA()"
                class="fade-in transition-all duration-300 ease-in-out"
                [matTooltip]="
                  _submitDisabled.disabled && _submitDisabled.reasonKey
                    ? (_submitDisabled.reasonKey
                      | translate: _submitDisabled.reasonParams)
                    : _submitTooltip.i18nKey
                      ? (_submitTooltip.i18nKey
                        | translate: _submitTooltip.i18nParams)
                      : ''
                "
                matTooltipPosition="above"
              >
                <svg class="fade-in" sdUseArrowIcon="arrow-up"></svg>
              </button>
            }
          </div>
        </div>
      </div>
    </div>
  `,
})
export class ConversationPromptInputSmartComponent
  implements AfterViewInit, OnInit, OnDestroy
{
  public readonly isThinking = input.required<boolean>();
  public readonly isNewReplyAnimating = input<boolean>(false);
  public readonly textarea =
    viewChild<ElementRef<HTMLTextAreaElement>>('textarea');
  public readonly fileUploadButton =
    viewChild<ElementRef<HTMLButtonElement>>('fileUploadButton');

  private readonly translateService = inject(TranslateService);
  private readonly featureFlagsStore = inject(
    Store<fromFeatureFlags.FeatureFlagsState>,
  );
  private readonly chatsStore = inject<Store<ChatsState>>(Store);
  private readonly chatsHelper = inject(ChatsHelper);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly azureSpeechToTextService =
    inject<SpeechToTextServiceInterface>(SpeechToTextAzureService);
  private readonly deepgramSpeechToTextService =
    inject<SpeechToTextServiceInterface>(SpeechToTextDeepgramService);
  private readonly sttService = computed(() => {
    if (this.ffDeepgram()) {
      return this.deepgramSpeechToTextService;
    }
    return this.azureSpeechToTextService;
  });
  public readonly sttState = computed(() => {
    return this.sttService().state();
  });
  protected readonly selectedChatUuid = this.chatsStore.selectSignal(
    chatsFeature.selectSelectedChatUuid,
  );
  /** KD-46: the skill whose settings and documents produce the answers. */
  private readonly skillThatAnswers = this.chatsStore.selectSignal(
    fromShared.selectSkillThatAnswers,
  );
  private readonly isNewConversationWithSkillThatAnswers =
    this.chatsStore.selectSignal(
      fromShared.selectIsNewConversationWithSkillThatAnswers,
    );

  private readonly selectedChat = computed(() => {
    const selectedChat = this.chatsStore.selectSignal(
      chatsFeature.selectSelectedChat,
    )();
    if (!selectedChat) {
      return null;
    }
    return {
      ...selectedChat,
      title: this.chatsHelper.getChatTitle(selectedChat.title),
    };
  });
  /**
   * Same uuid on a general draft and on the default chat conversation it
   * becomes, so attaching a file before the first send does not reset it.
   */
  private readonly skillThatAnswersUuid = computed(
    () => this.skillThatAnswers()?.uuid ?? null,
  );
  /** Read confirmed connector exclusions from the current chat. */
  private readonly pendingConnectorUpdates = this.chatsStore.selectSignal(
    chatsFeature.selectPendingConnectorUpdates,
  );
  protected readonly connectorsSaving = computed(
    () =>
      (this.pendingConnectorUpdates()[this.selectedChatUuid() ?? ''] ?? 0) > 0,
  );
  protected readonly connectorSurface = computed(
    () => this.skillThatAnswers()?.trait ?? 'general',
  );

  protected readonly chatDisabledConnectorIds = computed<readonly string[]>(
    () => this.selectedChat()?.disabledConnectorIds ?? [],
  );

  private readonly isMessageTooLong = computed(() => {
    return this.userInput().length > MAX_USER_INPUT_LENGTH;
  });
  /**
   * This computed returns the documents associated with the selected chat grouped into:
   * - fromFileUpload: Files being uploaded
   * - fromChat: Documents attached to chat but not yet in messages
   * - fromUserChatMessages: Documents attached to the user's chat messages
   * - fromChatAssistant: Documents belonging to the chat's assistant
   */
  private readonly selectedChatDocuments = computed(() => ({
    fromFileUpload: this.fileUploadStore.getFiles(),
    ...this.chatsStore.selectSignal(fromShared.selectSelectedChatDocuments)(),
  }));
  private readonly localFilesUuids = computed(() =>
    this.fileUploadStore.getFiles().map((f) => f.uuid),
  );

  protected readonly isChatUnavailable = this.chatsStore.selectSignal(
    fromShared.selectIsChatUnavailable,
  );
  protected readonly limits = this.chatsStore.selectSignal(
    fromShared.selectDocumentLimits,
  );
  protected readonly remainingUploadSlots = computed(() =>
    this.chatsStore.selectSignal(
      fromShared.selectRemainingUploadSlots(this.localFilesUuids()),
    )(),
  );
  private readonly pendingChatDocumentsStatus = this.chatsStore.selectSignal(
    fromShared.selectPendingChatDocumentsStatus,
  );
  private readonly promptTextRequired = this.chatsStore.selectSignal(
    fromShared.selectIsPromptTextRequired,
  );
  private readonly promptOverlayTooltipFromStore = this.chatsStore.selectSignal(
    fromShared.selectPromptOverlayTooltip,
  );
  private readonly currentUploadRole = this.chatsStore.selectSignal(
    fromShared.selectCurrentUploadRole,
  );
  protected readonly documentCounts = computed(() =>
    this.chatsStore.selectSignal(
      fromShared.selectDocumentCounts(this.localFilesUuids()),
    )(),
  );
  protected readonly fileUploadDisabled = computed(() =>
    this.chatsStore.selectSignal(
      fromShared.selectFileUploadDisabled(this.localFilesUuids()),
    )(),
  );
  protected readonly promptInputHidden = this.chatsStore.selectSignal(
    fromShared.selectPromptInputHidden,
  );
  protected readonly submitButtonTooltip = computed(() =>
    this.chatsStore.selectSignal(
      fromShared.selectSubmitButtonTooltip(this.localFilesUuids()),
    )(),
  );
  /** Args for the combined file-upload error toast selector (document + file-upload store errors). */
  private readonly fileUploadErrorToastArgs = computed(
    (): fromShared.FileUploadErrorToastSelectorArgs => {
      const files = this.fileUploadStore.getFiles();
      return {
        localFilesUuids: this.localFilesUuids(),
        fileUploadErrors: {
          hasUploadError: files.some((f) => f.error === 'Error uploading file'),
          // 'Empty file' (D4/SB-3437) used to fold into the generic
          // upload-error flag above, which left chat saying only "Some file(s)
          // could not be uploaded." and naming no cause. Reported by Louis on
          // 2026-09-02; it now carries the SB-3515 spec's §4 empty-file string.
          hasEmptyFile: files.some((f) => f.error === 'Empty file'),
          hasInvalidType: files.some((f) => f.error === 'Invalid file type'),
          // Which size RULES fired, deduplicated — not how many files each
          // refused (SB-3515 T7). Read per file from the same helper that did
          // the refusing, so the toast can never quote a limit the validator
          // did not apply: a 40 MB `.json` and a 600 MB `.pdf` in one drop
          // produce two messages with 25 and the tenant's cap respectively.
          sizeExceededCaps: fromShared.dedupeSizeCaps(
            files
              .filter((f) => f.error === 'File size exceeded')
              .map((f) => ({
                cap: UploadValidators.effectiveUploadCap(f).cap,
                limitInMB: fromShared.effectiveUploadCapInMB(f),
              })),
          ),
          hasReservedFilename: files.some(
            (f) => f.error === 'Reserved filename',
          ),
        },
      };
    },
  );

  /**
   * State of the file upload error toast (document count/limits/processing + upload/type/size errors).
   * Selector holds all logic; component only translates and builds visibility/dismissable.
   */
  public readonly fileUploadErrorToastState = computed(() => {
    const messages = this.chatsStore.selectSignal(
      fromShared.selectFileUploadErrorToastMessages(
        this.fileUploadErrorToastArgs(),
      ),
    )();
    const translated = messages.map(
      (toast) =>
        this.translateService.instant(
          toast.i18nKey,
          toast.i18nParams,
        ) as string,
    );
    return {
      isVisible: translated.length > 0,
      message: translated.join(' '),
      dismissable: this.promptInputHidden(),
    };
  });

  public readonly fileUploadStore = inject(fromShared.FileUploadV2Store);
  private readonly imagePreview = inject(fromShared.ChatImagePreviewStore);
  private readonly chatCreation = inject(ChatCreationService);
  private readonly viewTransitions = inject(ViewTransitionTracker);
  /** The chat the composer last reset for: tells a re-key from a switch. */
  private composerChatUuid: string | null = null;
  public readonly ffDeepgram = this.featureFlagsStore.selectSignal(
    fromFeatureFlags.featureFlagsFeature.selectFeatureFlag('ff-deepgram'),
  );
  public readonly ffMcpConnectors = this.featureFlagsStore.selectSignal(
    fromFeatureFlags.featureFlagsFeature.selectMcpConnectors,
  );
  public readonly isFileUploadLoading = computed(() => {
    // The preflight window: a dropped file still being measured is not in the
    // store yet. Without this, a ready file already attached would let an
    // empty-box send go out without it (SB-3538 T4).
    return (
      this.fileUploadStore.state().loading ||
      this.fileUploadStore.hasPendingPreflight() ||
      this.pendingChatDocumentsStatus().hasProcessing
    );
  });

  public readonly tooltipOverPromptText = computed(() => {
    const overlay = this.promptOverlayTooltipFromStore();
    if (overlay.i18nKey) return overlay.i18nKey;
    if (this.fileUploadStore.state().loading)
      return 'PROMPT_INPUT.TOOLTIP.ADD_SOURCE_PROCESSING_FILE';
    if (this.isMessageTooLong()) return 'PROMPT_INPUT.TOOLTIP.MESSAGE_TOO_LONG';
    return null;
  });
  private readonly fileUploadTooltip = computed(() =>
    this.chatsStore.selectSignal(
      fromShared.selectFileUploadDisabled(this.localFilesUuids()),
    )(),
  );
  public readonly fileUploadTooltipText = computed(() => {
    const tip = this.fileUploadTooltip();
    const reason = tip.i18nKey
      ? (this.translateService.instant(tip.i18nKey, tip.i18nParams) as string)
      : '';
    // The tooltip carries the DOCUMENT COUNT only. The extension list was
    // removed on 2026-08-27 (Ayman): with 24 accepted extensions the tooltip
    // rendered as a full-width band of comma-separated tokens that nobody reads,
    // and length was defeating the purpose of putting it there.
    //
    // 🚨 THIS LEAVES NO USER-REACHABLE LIST OF ACCEPTED FORMATS, and that is a
    // known consequence rather than an oversight. KD-14 deliberately moved the
    // exact list OFF the type-error toast (which names families — "text files",
    // "image files") and ONTO this tooltip, which made this the only place a
    // user could discover the extensions. Removing it here closes that path
    // entirely: a user refused for an unsupported type is told the family and
    // cannot find out which extensions the family contains.
    //
    // So do not "restore" the list to fix a support ticket about discoverability
    // without reopening the copy decision — the same conversation KD-14 and T24
    // (squadbox-app#1357) took to design and PM. The honest fix for that
    // complaint is a real affordance (a help link, a picker hint), not a longer
    // tooltip.
    //
    // The picker's own `accept` filter still narrows the file dialog, so the OS
    // greys out unsupported files. That is the remaining discovery route, and it
    // only works for users who open the dialog rather than dragging.
    return reason;
  });
  /** Submit disabled state plus reason (for tooltip/a11y). First matching condition wins. */
  public readonly submitDisabledResult = computed((): SubmitDisabledResult => {
    if (this.connectorsSaving())
      return {
        disabled: true,
        reasonKey: 'PROMPT_INPUT.PLUS_MENU.SAVING',
        reason: 'Saving connector preferences',
      };
    const counts = this.documentCounts();
    const limits = this.limits();
    const filesFromUpload = this.selectedChatDocuments().fromFileUpload;
    const notDisabled: SubmitDisabledResult = {
      disabled: false,
      reasonKey: '',
      reason: 'Submit allowed',
    };

    if (this.isChatUnavailable()) {
      return {
        disabled: true,
        reasonKey: 'PROMPT_INPUT.TOOLTIP.CHAT_UNAVAILABLE',
        reasonParams: {},
        reason: 'Chat is unavailable — assistant is not in ready status',
      };
    }
    if (isPromptTextMissing(this.promptTextRequired(), this.userInput())) {
      return {
        disabled: true,
        reasonKey: 'PROMPT_INPUT.TOOLTIP.MESSAGE_REQUIRED',
        reasonParams: {},
        reason: 'Prompt text is required but empty',
      };
    }
    if (this.isFileUploadLoading()) {
      return {
        disabled: true,
        reasonKey: 'PROMPT_INPUT.TOOLTIP.ADD_SOURCE_PROCESSING_FILE',
        reasonParams: {},
        reason: 'Files are still uploading or documents are processing',
      };
    }
    if (this.isMessageTooLong()) {
      return {
        disabled: true,
        reasonKey: 'PROMPT_INPUT.TOOLTIP.MESSAGE_TOO_LONG',
        reasonParams: {},
        reason: 'Message exceeds maximum length',
      };
    }
    if (this.isThinking()) {
      return {
        disabled: true,
        reasonKey: 'CHAT.LOADING',
        reasonParams: {},
        reason: 'Assistant is thinking',
      };
    }
    if (this.isNewReplyAnimating()) {
      return {
        disabled: true,
        reasonKey: 'PROMPT_INPUT.TOOLTIP.PLEASE_WAIT',
        reasonParams: {},
        reason: 'Please wait for the reply to finish',
      };
    }
    if (counts.inCurrentPhase > limits.max) {
      return {
        disabled: true,
        reasonKey: 'PROMPT_INPUT.TOOLTIP.ADD_SOURCE_FILE_LIMIT_REACHED',
        reasonParams: {},
        reason: `Document count (${counts.inCurrentPhase}) exceeds maximum (${limits.max})`,
      };
    }
    if (counts.inCurrentPhase < limits.min) {
      return {
        disabled: true,
        reasonKey: 'PROMPT_INPUT.TOOLTIP.ADD_SOURCE_FILE_UNDER_MIN_FILES',
        reasonParams: { minFiles: limits.min },
        reason: `Need at least ${limits.min} documents, have ${counts.inCurrentPhase}`,
      };
    }
    const failureMessageKeys =
      this.pendingChatDocumentsStatus().failureMessageKeys;
    if (failureMessageKeys.length > 0) {
      return {
        disabled: true,
        // A tooltip is one sentence, so the first distinct cause wins where the
        // toast lists them all. Reason-aware for the same purpose as the toast
        // (SB-3515 §4) — the two surfaces must not disagree about the cause.
        reasonKey: failureMessageKeys[0],
        reasonParams: {},
        reason: 'One or more documents failed processing',
      };
    }
    if (
      filesFromUpload.some(
        (file) =>
          file.status === 'upload-invalid' || file.status === 'upload-failed',
      )
    ) {
      return {
        disabled: true,
        reasonKey: 'PROMPT_INPUT.ADD_SOURCE_UPLOAD_ERROR',
        reasonParams: {},
        reason: 'One or more files failed to upload',
      };
    }
    return notDisabled;
  });

  public readonly inputPlaceholder: Signal<string> = computed(() => {
    switch (this.sttState()) {
      case 'starting':
        return 'PROMPT_INPUT.STT_STARTING';
      case 'transcripting':
        return 'PROMPT_INPUT.STT_LISTENING';
      case 'finalizing':
        return 'PROMPT_INPUT.STT_FINALIZING';
      default:
        return this.skillThatAnswers()?.trait === 'comparison' &&
          this.isNewConversationWithSkillThatAnswers()
          ? 'ASSISTANTS.TRAITS.COMPARISON.PLACEHOLDER_INPUT_TEXT'
          : 'PROMPT_INPUT.PLACEHOLDER';
    }
  });
  public readonly isSpeechButtonDisabled = computed(() =>
    this.isChatUnavailable(),
  );
  public readonly speechButtonTooltip = computed(() => {
    return this.isSpeechButtonDisabled()
      ? ''
      : 'PROMPT_INPUT.TOOLTIP.VOICE_TO_TEXT';
  });
  /**
   * Determines if the abort STT button should be disabled.
   * Enabled only when STT is actively processing: 'starting', 'transcripting', or 'finalizing'.
   * Disabled in transient/cleanup states like 'inactive', 'error', 'aborting', or 'completed'
   * since these states are already cleaning up and don't require user intervention.
   */
  public readonly isAbortSttDisabled = computed(() => {
    const state = this.sttState();
    return (
      state !== 'starting' &&
      state !== 'transcripting' &&
      state !== 'finalizing'
    );
  });
  /**
   * Determines if the finalize STT button should be disabled.
   * Enabled when state is 'transcripting' (normal case).
   */
  public readonly isFinalizeSttDisabled = computed(
    () => this.sttState() !== 'transcripting',
  );
  public readonly documentFileChips = computed(() => {
    const fromChatDocs = this.selectedChatDocuments().fromChat;
    const fromFileUploadFiles = this.selectedChatDocuments().fromFileUpload;
    const chips: {
      uuid: string;
      filename: string;
      fileType: fromSeriousSystem.FileTypeIconsEnum;
      isLoading: boolean;
      error: boolean;
      type: 'document' | 'file';
    }[] = [];

    fromChatDocs.forEach((document) => {
      chips.push({
        uuid: document.uuid,
        filename: document.filename,
        fileType: fromSeriousSystem.getFileTypeIconFromExtension(
          document.extension,
        ),
        isLoading: document.status === 'processing',
        type: 'document',
        error: document.status === 'failed',
      });
    });

    fromFileUploadFiles
      .filter((file) => !fromChatDocs.some((doc) => doc.uuid === file.uuid))
      .forEach((file) => {
        chips.push({
          uuid: file.uuid,
          filename: file.name,
          fileType: fromSeriousSystem.getFileTypeIconFromExtension(
            file.extension,
          ),
          isLoading:
            file.status === 'upload-ongoing' ||
            file.status === 'upload-pending',
          type: 'file',
          error:
            file.status === 'upload-invalid' || file.status === 'upload-failed',
        });
      });

    return chips;
  });

  /**
   * The batch of documents the server refused with the first message, keyed
   * like a batch (`autoSubmitBatchKey`); `null` when no refusal is on the chat
   * (SB-3944 / KD-41). It decides the "Send again" button only: the auto-send
   * itself lives in `ChatsAutoSendEffects` (KD-58).
   */
  private readonly refusedBatchKey = computed(() => {
    const refusal = this.selectedChat()?.firstSendRefusal;
    return refusal
      ? autoSubmitBatchKey(refusal.documentUuids.map((uuid) => ({ uuid })))
      : null;
  });

  public readonly removedPromptInputState = computed(() => {
    const documents = this.documentFileChips();
    const shouldRemovePromptInput = this.promptInputHidden();
    const limits = this.limits();

    if (!shouldRemovePromptInput) {
      return {
        shouldRemovePromptInput: false as const,
        shouldShowTransientFirstMessageWithDocuments: false as const,
        showSendAgain: false as const,
      } as const;
    }

    // Listed property by property, not spread: the template narrows this union
    // on `message`, which relies on TS normalizing the two object literals.
    const flags = deriveRemovedPromptInputFlags(documents, limits);
    const batchKey = autoSubmitBatchKey(documents);
    const refusedBatchKey = this.refusedBatchKey();
    const showSendAgain = isSendAgainShown(batchKey, refusedBatchKey);

    return {
      shouldRemovePromptInput,
      // No transient first message without a document to show on it.
      shouldShowTransientFirstMessageWithDocuments: documents.length > 0,
      shouldShowChatbotThinkingForDocuments:
        flags.shouldShowChatbotThinkingForDocuments && !showSendAgain,
      showSendAgain,
      message: {
        id: -1,
        content: '' as const,
        isFromUser: true as const,
        documents: documents.map((doc) => ({
          uuid: doc.uuid,
          filename: doc.filename,
          extension: doc.fileType,
          deletedAt: null,
          status: this.getDocumentStatus(doc),
          isError: doc.error ?? false,
          isLoading: doc.type === 'file' && doc.isLoading,
          isDisabled: false as const,
          isClickable: false as const,
          type: doc.type,
        })),
      } satisfies fromMessagesListing.MessageListingType,
    };
  });

  private readonly inputSubject = new Subject<void>();
  /**
   * The text of the selected chat's composer. It lives in the chats store,
   * keyed by chat uuid (SB-3944 / KD-47), so it survives a switch to another
   * chat and back inside the tab, and is lost on reload.
   */
  private readonly promptTextByChatUuid = this.chatsStore.selectSignal(
    chatsFeature.selectPromptTextByChatUuid,
  );
  public readonly userInput = computed(
    () => this.promptTextByChatUuid()[this.selectedChatUuid() ?? ''] ?? '',
  );

  // Recording timer state
  private readonly recordingSeconds = signal(0);
  private recordingIntervalId: NodeJS.Timeout | null = null;
  /** The composer text when dictation started; each dictation is appended to it. */
  private inputBeforeDictation = '';
  public readonly recordingTimeLabel = computed(() => {
    const totalSeconds = this.recordingSeconds();
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  });

  public readonly isMobile = inject<Store<fromLayout.LayoutState>>(
    Store,
  ).selectSignal(fromLayout.layoutFeature.selectIsMobile);

  private transcriptionCompletedSubscription: Subscription | null = null;
  private transcriptResultSubscription: Subscription | null = null;
  // The chat the current dictation started in (see `setSpeechInput`).
  private sttChatUuid: string | null = null;
  // The chat last marked as staging (KD-58), so the store hears each change once.
  private stagingChatUuid: string | null = null;
  // Whether each chat this composer showed had its prompt input hidden, read
  // at a switch for the chat being left: by then the selectors already read
  // the next one.
  private readonly promptHiddenByChatUuid = new Map<string, boolean>();
  private readonly chatEntities = this.chatsStore.selectSignal(
    chatsFeature.selectEntities,
  );
  private readonly ffSttDebugLog = this.featureFlagsStore.selectSignal(
    fromFeatureFlags.featureFlagsFeature.selectFeatureFlag<boolean>(
      'ff-stt-debug-log',
    ),
  );

  constructor() {
    effect(() => {
      this.transcriptionCompletedSubscription?.unsubscribe();
      this.transcriptionCompletedSubscription =
        this.sttService().transcriptionCompleted.subscribe(({ raw, clean }) => {
          const newInput = appendDictationToInput(
            this.inputBeforeDictation,
            clean,
          );
          if (this.ffSttDebugLog()) {
            // eslint-disable-next-line no-console
            console.log('STT Debug Log - transcriptionCompleted', {
              raw,
              clean,
              previousInput: this.userInput(),
              newInput,
            });
          }
          this.setSpeechInput(newInput);
          setTimeout(() => this.adjustTextareaHeight(), 100);
          this.stopSttTimer();

          // Fire voice_to_text_used event (SB-3513 T7)
          this.analyticsService
            .sendVoiceToTextUsedEvent({
              provider: this.ffDeepgram() ? 'deepgram' : 'azure',
              transcriptionClean: clean,
            })
            .subscribe();
        });

      this.transcriptResultSubscription?.unsubscribe();
      this.transcriptResultSubscription =
        this.sttService().streamResult.subscribe((result) => {
          if (this.ffDeepgram() && result.transcript) {
            this.setSpeechInput(
              appendDictationToInput(
                this.inputBeforeDictation,
                result.transcript,
              ),
            );
            setTimeout(() => this.adjustTextareaHeight(), 100);
          }
        });
    });

    /**
     * When the selected chat or assistant changes, resets the file upload
     * state. The typed text and the uploaded files are not cleared: they
     * belong to their chat (SB-3944 / KD-56, which amends KD-47), and its image
     * previews stay. A file still uploading is cancelled: the upload store is
     * one per page, and the upload-done effect below hands a finished file to
     * the chat selected at that moment, which would be the wrong one.
     * A re-key is not a change of chat (SB-3944 / KD-18): a 409 on the first
     * create moves the draft to a new uuid, and nothing resets.
     * A dictation still running is cancelled on a change of chat, like an
     * upload (SB-3995 on main): the words Deepgram already streamed stay in
     * the draft of the chat they were for, and nothing carries over.
     */
    effect(() => {
      const chatUuid = this.selectedChatUuid();
      this.skillThatAnswersUuid();

      const previous = this.composerChatUuid;
      this.composerChatUuid = chatUuid;
      if (
        previous &&
        chatUuid &&
        previous !== chatUuid &&
        this.chatCreation.latestUuid(previous) === chatUuid
      ) {
        return;
      }

      untracked(() => {
        // `reset()` cancels every file still staged: a registered file has
        // already left the upload store, so each one here goes with its
        // preview (Q16).
        const staged = this.fileUploadStore.getFiles();
        staged.forEach((file) => this.imagePreview.release(file.uuid));

        if (previous && previous !== chatUuid) {
          this.inputBeforeDictation = '';
          if (this.sttState() !== 'inactive') {
            this.abortStt();
          }
          this.emptyIfLeftWithRefusedFile(previous);
        }
      });
      this.fileUploadStore.reset();
      // The restored text of the chat gets its height once it is rendered.
      setTimeout(() => this.adjustTextareaHeight(), 0);
    });

    /** Records, per chat, whether its prompt input is hidden (see the switch effect). */
    effect(() => {
      const chatUuid = this.selectedChatUuid();
      const hidden = this.promptInputHidden();
      if (chatUuid) {
        this.promptHiddenByChatUuid.set(chatUuid, hidden);
      }
    });

    /**
     * SB-3944 / KD-58: tells the store which chat this composer is staging
     * files for, so the auto-send waits for it. The upload store is page-scoped
     * and the auto-send runs in the store, which cannot see it. A staged file
     * already registered on the chat does not count, nor does it in the chips.
     */
    effect(() => {
      const chatUuid = this.selectedChatUuid();
      const registered = new Set(
        this.selectedChatDocuments().fromChat.map((document) => document.uuid),
      );
      const staging =
        this.fileUploadStore.hasPendingPreflight() ||
        this.fileUploadStore
          .getFiles()
          .some((file) => !registered.has(file.uuid));
      untracked(() => this.setStagingChat(staging ? chatUuid : null));
    });

    /**
     * When the selected chat or assistant changes:
     * - Re-primes the upload store's assistantForAnalytics context
     * (separate from the chat-switch effect above to track store state independently)
     */
    effect(() => {
      const assistant = this.skillThatAnswers();
      untracked(() => {
        if (assistant) {
          const assistantForAnalytics = toAssistantForAnalytics(assistant);
          const { accessPath, group } =
            fromAnalyticsHelper.toSkillAnalyticsContext(assistant);
          // Deliberately NOT `toSkillAnalyticsContext(...).teammate`, which
          // takes an unordered `teammates[0]`. The two audio events use the
          // strict N=1 collapse, matching `voice_to_text_used` on this same
          // surface. N>1 pick rule: issue #1216 — change both readings together.
          const teammate = fromAnalyticsHelper.toSingleTeammateForAnalytics(
            assistant.teammates,
          );
          this.fileUploadStore.setUploadAssistant(
            assistantForAnalytics,
            accessPath,
            group,
            teammate,
          );
        }
      });
    });

    /**
     * Attaches files to the chat as documents when:
     * - the file-upload state is valid and
     * - the file-upload state files are all uploaded
     * - the chat has an id
     * then, removes the files from the file upload store.
     */
    /**
     * Capture a same-session preview blob for each staged IMAGE
     * file the moment it appears (idempotent), so the overlay (and, later, the
     * in-thread thumbnail) can render it across the ingestion gap. Must run
     * before the upload-done effect below drops the raw File.
     */
    effect(() => {
      this.fileUploadStore.getFiles().forEach((file) => {
        if (file.file && this.isImageExtension(file.extension)) {
          this.imagePreview.capture(file.uuid, file.file);
        }
      });
    });

    effect(() => {
      const uploadedFiles = this.fileUploadStore
        .getFiles()
        .filter((file) => file.status === 'upload-done');
      if (!uploadedFiles.length) return;

      untracked(() => {
        const chatUuid = this.selectedChatUuid();
        if (!chatUuid) return;

        const documentRole = this.currentUploadRole();

        uploadedFiles.forEach((file) => {
          // A completed chat upload always carries its real transaction id;
          // `null` only ever marks a builder-seeded persisted doc (never present
          // on this chat store), so skipping it is defensive, not reachable here.
          if (file.transactionId === null) return;
          this.chatsStore.dispatch(
            ChatWithMessagesActions.addDocuments({
              transactionId: file.transactionId,
              chatUuid,
              files: [file],
              documentRole,
            }),
          );
        });
        this.fileUploadStore.removeFiles(
          uploadedFiles.map((file) => file.uuid),
        );
      });
    });

    /**
     * Set the focus on the textarea (prompt-input)
     * - when the chatbot stops thinking
     * - when the selected assistant or chat is changed
     */
    effect(() => {
      this.selectedChatUuid();
      this.skillThatAnswersUuid();

      setTimeout(() => {
        if (this.textarea() && !this.isThinking()) {
          this.textarea()?.nativeElement.focus();
        }
      }, 0);
    });

    // The auto-send of a hidden-prompt skill lives in `ChatsAutoSendEffects`,
    // which watches every chat (SB-3944 / KD-58).
  }

  ngOnInit() {
    this.inputSubject.pipe(debounceTime(50)).subscribe(() => {
      setTimeout(() => this.adjustTextareaHeight(), 0);
    });
  }

  ngAfterViewInit() {
    this.adjustTextareaHeight();
  }

  ngOnDestroy() {
    this.transcriptionCompletedSubscription?.unsubscribe();
    this.transcriptResultSubscription?.unsubscribe();
    this.abortStt();
    // The page is left (the upload store goes with it): the same rule as a
    // chat switch, before the mark clears and the auto-send could fire.
    if (this.composerChatUuid) {
      this.emptyIfLeftWithRefusedFile(this.composerChatUuid);
    }
    // A mark left behind would hold that chat's auto-send for good.
    this.setStagingChat(null);
  }

  /**
   * Maxim, 2026-10-14: a hidden-prompt chat left with a refused file comes
   * back empty, as closing its error toast leaves it, and the person starts
   * over in that chat. Without this, the upload store's reset (a chat switch)
   * or its end (the page left) drops the error chip, and the auto-send sends
   * the rest alone. A regular chat keeps its files: it waits for Send.
   */
  private emptyIfLeftWithRefusedFile(chatUuid: string): void {
    const leftWithRefusedFile =
      this.promptHiddenByChatUuid.get(chatUuid) === true &&
      this.fileUploadStore
        .getFiles()
        .some(
          (file) =>
            file.status === 'upload-invalid' || file.status === 'upload-failed',
        );
    if (!leftWithRefusedFile) {
      return;
    }
    (this.chatEntities()[chatUuid]?.documents ?? []).forEach((document) =>
      this.imagePreview.release(document.uuid),
    );
    this.chatsStore.dispatch(
      ChatWithMessagesActions.removeDocuments({ chatUuid }),
    );
  }

  private setStagingChat(chatUuid: string | null): void {
    if (chatUuid === this.stagingChatUuid) {
      return;
    }
    this.stagingChatUuid = chatUuid;
    this.chatsStore.dispatch(ChatActions.setStagingChat({ chatUuid }));
  }

  public handleInput(event: Event) {
    if (this.sttState() === 'inactive') {
      this.setUserInput((event.target as HTMLTextAreaElement).value);
      this.inputSubject.next();
    }
  }

  public onPaste() {
    setTimeout(() => this.adjustTextareaHeight(), 0);
  }

  public adjustTextareaHeight(reset = false) {
    const textarea = this.textarea()?.nativeElement;
    if (!textarea) return;

    if (reset || !textarea.value) {
      textarea.style.height = `${TEXTAREA_MIN_HEIGHT}px`;
      return;
    }

    // Create a hidden clone to measure content
    const clone = textarea.cloneNode(true) as HTMLTextAreaElement;
    clone.style.position = 'absolute';
    clone.style.visibility = 'hidden';
    clone.style.height = 'auto';
    clone.style.minHeight = `${TEXTAREA_MIN_HEIGHT}px`;
    clone.style.maxHeight = 'none';
    clone.style.transition = 'none';
    clone.style.transitionDuration = '0s';
    clone.style.width = `${textarea.clientWidth}px`;
    clone.rows = 1;
    clone.value = textarea.value;

    document.body.appendChild(clone);
    const contentHeight = clone.scrollHeight;
    clone.remove();

    if (contentHeight < TEXTAREA_MAX_HEIGHT) {
      textarea.classList.add('hide-scrollbar');
    } else {
      textarea.classList.remove('hide-scrollbar');
    }

    const newHeight = Math.min(contentHeight, TEXTAREA_MAX_HEIGHT);
    textarea.style.height = `${newHeight}px`;

    if (this.sttState() === 'transcripting') {
      textarea.scrollTop = textarea.scrollHeight;
    }
  }

  private submitPrompt() {
    this.addChatMessage(toSendableMessageContent(this.userInput()));
    this.clear();
  }

  public handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!this.submitDisabledResult().disabled) {
        this.submitPrompt();
      }
    }
  }

  /** Persist exclusions only once the chat exists and no save is pending. */
  protected handleChangeDisabledConnectors(
    disabledConnectorIds: string[],
  ): void {
    const uuid = this.selectedChatUuid();
    if (!uuid || this.connectorsSaving()) {
      return;
    }
    this.chatsStore.dispatch(
      ChatsActions.setChatConnectors({
        uuid,
        disabledConnectorIds,
      }),
    );
  }

  public handleClickSubmitPromptCTA() {
    if (!this.submitDisabledResult().disabled) {
      this.submitPrompt();
    }
  }

  private setUserInput(text: string): void {
    const chatUuid = this.selectedChatUuid();
    if (chatUuid) {
      this.chatsStore.dispatch(ChatActions.setPromptText({ chatUuid, text }));
    }
  }

  /**
   * A transcript belongs to the chat where the dictation started. One that
   * lands after another chat was selected is dropped, so it cannot overwrite
   * that chat's saved draft.
   */
  private setSpeechInput(text: string): void {
    if (this.sttChatUuid !== this.selectedChatUuid()) return;
    this.setUserInput(text);
  }

  public clear() {
    this.setUserInput('');
    this.adjustTextareaHeight(true);
  }

  public removeFile(uuid: string) {
    const chatUuid = this.selectedChatUuid();
    if (!chatUuid) {
      return;
    }
    this.chatsStore.dispatch(
      ChatWithMessagesActions.removeDocument({
        chatUuid,
        documentUuid: uuid,
      }),
    );
    this.fileUploadStore.removeFile(uuid);
    // Drop the same-session preview blob for a chip removed before send.
    this.imagePreview.release(uuid);
  }

  private isImageExtension(extension: string): boolean {
    return (
      fromSeriousSystem.getFileTypeIconFromExtension(extension) ===
      fromSeriousSystem.FileTypeIconsEnum.image
    );
  }

  /**
   * Open the full-screen overlay for a staged image chip.
   * No-op for non-image chips — a document chip has no pre-send preview.
   * The chip's × (remove) stops propagation, so this does not fire on remove.
   */
  public openImageOverlayFromComposer(chip: {
    uuid: string;
    filename: string;
    fileType: fromSeriousSystem.FileTypeIconsEnum;
  }) {
    if (chip.fileType !== fromSeriousSystem.FileTypeIconsEnum.image) {
      return;
    }
    this.chatsStore.dispatch(
      fromShared.ImageOverlayActions.openImageFromComposer({
        uuid: chip.uuid,
        filename: chip.filename,
      }),
    );
    // Track overlay open from a composer image chip.
    this.analyticsService
      .sendImageOverlayOpenedEvent({ source: 'chip' })
      .subscribe();
  }

  /**
   * Sends the refused batch again: the same call the auto-send makes, started
   * by the person (SB-3944 / KD-41).
   */
  public sendAgain(): void {
    this.addChatMessage('');
  }

  public addChatMessage(content: string) {
    if (this.connectorsSaving()) return;
    const selectedChat = this.selectedChat();

    if (!selectedChat) {
      return;
    }

    const dispatchChatMessage = () => {
      this.chatsStore.dispatch(
        ChatWithMessagesActions.addChatMessage({
          chatUuid: selectedChat.uuid,
          content,
          documents: selectedChat.documents,
        }),
      );
    };

    // SB-3944 / KD-59: tracked, so the address replace that follows a first
    // send's create waits for this transition to end.
    this.viewTransitions.run(dispatchChatMessage);
  }

  private startSttTimer(): void {
    this.clearSttInterval();
    this.recordingSeconds.set(0);
    this.recordingIntervalId = setInterval((): void => {
      this.recordingSeconds.update((v) => v + 1);
    }, 1000);
  }

  private stopSttTimer(): void {
    this.clearSttInterval();
    this.recordingSeconds.set(0);
  }

  private clearSttInterval(): void {
    if (this.recordingIntervalId !== null) {
      clearInterval(this.recordingIntervalId);
      this.recordingIntervalId = null;
    }
  }

  public handleClickStartSttCTA() {
    this.sttChatUuid = this.selectedChatUuid();
    this.inputBeforeDictation = this.userInput();
    void this.sttService().start();
    this.startSttTimer();
  }

  public handleClickFinalizeSttCTA() {
    this.sttService().finalize();
  }

  public handleClickAbortSttCTA() {
    // Discard only this dictation; text from before it is kept.
    this.setUserInput(this.inputBeforeDictation);
    setTimeout(() => this.adjustTextareaHeight(), 100);
    this.abortStt();
  }

  private abortStt() {
    this.sttService().abort();
    this.stopSttTimer();
  }

  public triggerFileUpload(from: FileUploadedEvent['from']) {
    this.fileUploadStore.setUploadFrom(from);
    this.fileUploadButton()?.nativeElement.click();
  }

  private getDocumentStatus(doc: {
    error: boolean;
    isLoading: boolean;
    type: 'document' | 'file';
  }): 'failed' | 'processing' | 'ready' {
    if (doc.error) return 'failed';
    if (doc.isLoading) return 'processing';
    return 'ready';
  }
}
